import React, { Fragment, useContext, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { DictType, Task } from "@pma/types";
import {
  ADD_TASK_DEPENDENCY_ENDPOINT,
  REMOVE_TASKS_DEPENDENCY_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants";
import { Separator } from "@components/common/separator";
import { NetworkIcon, Plus, Trash2 } from "lucide-react";
import { Button } from "@components/common/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { Badge } from "@components/common/badge";
import { toast } from "@hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTranslator from "@hooks/use-translator";
import { ScrollArea } from "@components/common/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/common/accordion";
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider";

interface Props {
  currentTask: Task;
  tasks: Array<DictType>;
  revalidate?: CallableFunction;
  parentTaskId?: number;
}
const TaskDependencies = ({ currentTask, tasks, parentTaskId }: Props) => {
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const navigate = useNavigate();

  const { translate } = useTranslator();
  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam); 
  const { setTaskExpandForLoading } = useContext(GroupedTasksContext);

  const createTaskDependency = async (
    depend_on_task_id: Task[],
    task_id: number,
  ) => {
    if (!depend_on_task_id || depend_on_task_id?.length === 0) {
      return;
    }
    const recipients = depend_on_task_id?.map((t) => t.id);
    const response = await axios.post(
      SERVER_URL + ADD_TASK_DEPENDENCY_ENDPOINT,
      {
        depend_on_tasks_id: recipients,
        task_id: task_id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response?.data;
  };

  const removeTaskDependency = async (
    task_id: number,
    depend_on_task_id: number,
  ) => {
    const response = await axios.post(
      SERVER_URL + REMOVE_TASKS_DEPENDENCY_ENDPOINT,
      {
        depend_on_task_id: depend_on_task_id,
        task_id: task_id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response?.data;
  };
  const createTaskDependencyMutation = useMutation({
    mutationKey: ["add-task-dependency"],
    mutationFn: (task: any) =>
      createTaskDependency([task] as Task[], currentTask.id),
    onSuccess: (data) => {
      // Trigger Revalidate tasks
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({queryKey:['grouped-tasks']})
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
      }
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: translate(error?.response?.data?.message || "An error occurred"),
        variant: "error",
      });
    },
  });

  const removeTaskDependencyMutation = useMutation({
    mutationKey: ["delete-task-dependency"],
    mutationFn: ({ taskId, dependencyId }: DictType) =>
      removeTaskDependency(taskId, dependencyId),
    onSuccess: (data) => {
      // Trigger Revalidate tasks 
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
      queryClient.invalidateQueries({queryKey:['grouped-tasks']})
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any);
      }
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: translate(error?.response?.data?.message || "An error occurred"),
        variant: "error",
      });
    },
  });

  const findTask = (taskId: number) => {
    const task = tasks?.filter((task) => task?.id === taskId)[0];

    return task as Task;
  };

  const availableTasks = tasks?.filter((task) => task?.id !== currentTask?.id);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="link" className="relative">
          <NetworkIcon size={16} />
          {currentTask?.depends_on?.length > 0 && (
            <small className="text-[9px] absolute top-0 right-2 flex items-center">
              *{currentTask?.depends_on?.length}
            </small>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96">
        <div className="flex justify-between space-x-4 w-full">
          <div className="space-y-1 w-full">
            <h4 className="text-sm font-semibold">
              {translate("Task Dependencies")}
            </h4>

            <div className="text-sm">
              {currentTask?.depends_on?.length > 0 ? (
                <>
                  {currentTask?.depends_on?.map((id) => (
                    <Fragment key={id}>
                      <div className="flex items-center justify-between py-2 gap-2">
                        <p>
                          {currentTask?.title}
                          <strong className="text-primary text-xs">
                            {" depends on  "}
                          </strong>
                          {findTask(id)?.title}
                        </p>
                        <Trash2
                          onClick={() =>
                            removeTaskDependencyMutation.mutate({
                              taskId: currentTask?.id,
                              dependencyId: id,
                            })
                          }
                          size={10}
                          className="text-red-500 cursor-pointer"
                        />
                      </div>
                      <Separator style={{ border: "1px solid dash" }} />
                    </Fragment>
                  ))}
                </>
              ) : (
                <small>{translate("No dependencies found")}</small>
              )}
            </div>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="tasks-dependencies">
                <AccordionTrigger>
                  <span className="text-xs font-semibold text-left text-muted-foreground">
                    {translate("Add Dependency")}{" "}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ScrollArea className="h-[200px] flex flex-wrap">
                    {availableTasks?.map((task) => (
                      <Badge
                        variant={"outline"}
                        className="cursor-pointer hover:bg-primary/70 m-1 hover:text-white hover:animate-pulse ease-linear"
                        onClick={() =>
                          createTaskDependencyMutation.mutate(task)
                        }
                        key={task?.id}
                      >
                        <span className="mr-2 capitalize">{task?.title}</span>{" "}
                        <Plus size={13} className="" />
                      </Badge>
                    ))}
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TaskDependencies;
